<template>
  <v-snackbar
    class="nx-search-replace-toolbar"
    dark
    :timeout="0"
    :value="active"
    pa-0
  >
    <v-text-field dark v-model="searchText" :placeholder="$t('message.block.searchReplace.search')"
                  autofocus tabindex="0">
      <template v-slot:append v-if="searchText && hasOccurrences">
        <span class="body-1 mt-2">{{ currentIndex + 1 }} / {{ occurrencesIndices.length }}</span>
      </template>
    </v-text-field>
    <v-btn class="ml-1 mr-0 px-0" icon small @click="prev()">
      <v-icon>keyboard_arrow_up</v-icon>
    </v-btn>
    <v-btn class="ml-0 mr-1 px-0" icon small @click="next()">
      <v-icon>keyboard_arrow_down</v-icon>
    </v-btn>
    <v-text-field dark v-model="replaceText" class="ml-5" :placeholder="$t('message.block.searchReplace.replaceText')"
                  tabindex="1"></v-text-field>
    <v-spacer></v-spacer>
    <v-btn flat @click="replace">{{ $t('message.block.searchReplace.replace') }}</v-btn>
    <v-btn flat @click="replaceAll">{{ $t('message.block.searchReplace.replaceAll') }}</v-btn>
  </v-snackbar>
</template>

<script>
import Quill from 'quill';
import debounce from 'lodash/debounce';

export default {
  props: {
    active: Boolean,
    quill: Quill,
  },

  data() {
    return {
      searchText: '',
      replaceText: '',
      occurrencesIndices: [],
      currentIndex: 0,
    };
  },

  computed: {
    hasOccurrences() {
      return this.occurrencesIndices.length > 0;
    },
  },

  methods: {
    getIndicesOf(currentString, searchString) {
      let startIndex = 0;
      let index;
      const indices = [];
      // eslint-disable-next-line no-cond-assign
      while ((index = currentString.toLowerCase().indexOf(searchString.toLowerCase(), startIndex)) > -1) {
        indices.push(index);
        startIndex = index + searchString.length;
      }
      return indices;
    },

    // eslint-disable-next-line func-names
    debouncedSearch: debounce(function () {
      this.currentIndex = 0;
      this.search();
    }, 400),

    getText() {
      return this.quill.getContents().map((op) => {
        if (typeof op.insert === 'string') {
          return op.insert;
        }
        return '#';
      }).join('');
    },

    search() {
      console.log(`searching for term ${this.searchText}...`);

      //  remove any previous search
      this.removeStyle();
      if (!this.searchText) {
        return;
      }
      const totalText = this.getText();
      const match = this.getText().toLowerCase().includes(this.searchText.toLowerCase());
      if (match) {
        this.occurrencesIndices = this.getIndicesOf(totalText, this.searchText);
        this.occurrencesIndices.forEach(index => this.quill.formatText(index, this.searchText.length, 'searchedString', {
          index,
          selected: index === this.occurrencesIndices[this.currentIndex],
        }));
      } else {
        this.occurrencesIndices = [];
        this.currentIndex = 0;
      }

      if (this.currentIndex >= this.occurrencesIndices.length) {
        this.currentIndex = 0;
      }

      console.log(`matches for term ${this.searchText}: ${this.occurrencesIndices.length}`);
    },

    moveSelectedMatchHighlight(prevIndex, currentIndex) {
      if (prevIndex) {
        this.quill.formatText(prevIndex, this.searchText.length, 'searchedString', {
          index: prevIndex,
          selected: false,
        });
      }
      if (currentIndex) {
        this.quill.formatText(currentIndex, this.searchText.length, 'searchedString', {
          index: currentIndex,
          selected: true,
        });
      }
    },

    focusSelectedMatch(matchIndex) {
      this.quill.setSelection(matchIndex);
      const element = document.querySelector('.ql-searched-string[data-selected]');
      const bounding = element.getBoundingClientRect();

      const isInViewport = bounding.top >= 120
        && bounding.left >= 0
        && bounding.bottom <= (window.innerHeight || document.documentElement.clientHeight)
        && bounding.right <= (window.innerWidth || document.documentElement.clientWidth);

      if (isInViewport) {
        return;
      }

      const elementRect = element.getBoundingClientRect();
      const absoluteElementTop = elementRect.top + window.pageYOffset;
      const middle = absoluteElementTop - (window.innerHeight / 2);
      window.scrollTo(0, middle);
    },

    prev() {
      const prevIndex = this.currentIndex;
      this.currentIndex = this.currentIndex - 1 < 0 ? 0 : this.currentIndex - 1;
      this.moveSelectedMatchHighlight(this.occurrencesIndices[prevIndex], this.occurrencesIndices[this.currentIndex]);
      this.focusSelectedMatch(this.occurrencesIndices[this.currentIndex]);
    },

    next() {
      const prevIndex = this.currentIndex;
      this.currentIndex = this.currentIndex + 1 < this.occurrencesIndices.length ? this.currentIndex + 1 : 0;
      this.moveSelectedMatchHighlight(this.occurrencesIndices[prevIndex], this.occurrencesIndices[this.currentIndex]);
      this.focusSelectedMatch(this.occurrencesIndices[this.currentIndex]);
    },

    replace() {
      if (!this.searchText) {
        return;
      }

      if (this.occurrencesIndices.length === 0) {
        return;
      }

      this.quill.deleteText(this.occurrencesIndices[this.currentIndex], this.searchText.length);
      this.quill.insertText(this.occurrencesIndices[this.currentIndex], this.replaceText);
      this.quill.formatText(
        this.occurrencesIndices[this.currentIndex],
        this.searchText.length,
        'searchedString',
        false,
      );
      // update the occurrencesIndices.
      this.search();
    },

    replaceAll() {
      if (!this.searchText) {
        return;
      }

      while (this.occurrencesIndices.length > 0) {
        this.quill.deleteText(this.occurrencesIndices[0], this.searchText.length);
        this.quill.insertText(this.occurrencesIndices[0], this.replaceText);
        // update the occurrences indices.
        this.occurrencesIndices.shift();
        const indexDifference = this.replaceText.length - this.searchText.length;
        this.occurrencesIndices.forEach((occurrence, index) => {
          this.occurrencesIndices[index] = occurrence + indexDifference;
        });
      }
      // update the occurrencesIndices.
      this.search();
    },

    removeStyle() {
      this.quill.formatText(0, this.getText().length, 'searchedString', false);
    },
  },

  watch: {
    searchText() {
      this.debouncedSearch();
    },
  },

  beforeDestroy() {
    this.removeStyle();
  },
};
</script>

<style lang="stylus">
.nx-search-replace-toolbar {
  .v-snack__wrapper {
    max-width: none;
  }

  .v-snack__content {
    max-width: 80vw
    height: 52px
  }
}
</style>
